import { medium, BodyText, HeadingText } from '@unmind/design-system';
import { FontSizes } from '@unmind/design-system/theme/deprecated';
import { rem } from 'polished';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import getSubdomainFromUrl from 'utils/getSubdomainFromUrl';
import { useSubdomainInfo } from 'LoggedOut/SignUp/useSubdomainInfo';
import { tracking } from 'App/Tracking';
import { getCountryContact_helpCountryContact_contactDetails as ContactDetails } from './hooks/__generated__/getCountryContact';
import { getEapServices_helpEAPServices as EapService } from './hooks/__generated__/getEapServices';
import { HelpIcon } from './HelpIcons';
import { ContactService } from './components';

const TabMenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${rem(52)};

  ${medium(css`
    flex-direction: row;
  `)};
`;

const TabList = styled.div`
  display: flex;
  flex-direction: column;
  min-width: ${rem(296)};
  margin-bottom: ${rem(40)};
  & > * + * {
    margin-top: ${rem(12)};
  }
  flex: 1;

  ${medium(css`
    flex: 0;
  `)};
`;

const Tab = styled.button<{ active: boolean }>`
  padding: ${rem(12)};
  background: ${({ active, theme }) =>
    active
      ? theme.colors.background.secondary
      : theme.colors.background.primary};
  border: none;
  border-radius: ${rem(12)};
  cursor: pointer;
  display: flex;
  align-items: center;
  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
  }
  gap: ${rem(8)};
`;

const TabTitle = styled(BodyText).attrs(({ theme }) => ({
  sizes: [FontSizes.fontSize16],
  color: theme.colors.text.primary,
}))`
  line-height: ${rem(16)};
`;

const TabContent = styled.div`
  flex: 1;
  margin-left: 0;
  background: ${({ theme }) => theme.colors.background.card};
  border-radius: ${rem(12)};
  padding: ${rem(24)};

  ${medium(css`
    margin-left: ${rem(32)};
  `)};
`;

const ServiceDetailHeader = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.secondary};
  padding-bottom: ${rem(24)};
`;

const ServiceDetailTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${rem(24)};

  ${medium(css`
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  `)};
`;

/* eslint-disable @typescript-eslint/no-explicit-any */
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
const ServiceDetailTitle = styled(HeadingText).attrs(({ theme }) => ({
  sizes: [
    theme.typography.fontSizes.fontSize18,
    theme.typography.fontSizes.fontSize24,
  ],
  weight: theme.typography.fontWeights.semiBold,
  level: 2,
}))`
  line-height: 100%;
  margin-bottom: ${rem(24)};

  ${medium(css`
    margin-bottom: 0;
  `)};
` as any;
/* eslint-enable @typescript-eslint/no-explicit-any */

const ServiceDetailSubtitle = styled(BodyText).attrs(({ theme }) => ({
  sizes: [
    theme.typography.fontSizes.fontSize14,
    theme.typography.fontSizes.fontSize16,
  ],
  color: theme.colors.text.secondary,
}))``;

const ServiceDetailDescription = styled(BodyText).attrs(({ theme }) => ({
  forwardedAs: 'div',
  sizes: [theme.typography.fontSizes.fontSize14],
}))`
  line-height: 140%;
  color: ${({ theme }) => theme.colors.text.primary};

  li:not(:last-child) {
    margin-bottom: ${rem(8)};
  }

  a {
    font-size: inherit;
  }
`;

interface HelpServicesTabsProps {
  contactDetails?: ContactDetails[];
  eapServices: EapService[];
  countryCode: string;
}

/**
 * This is a custom vertical tab component that displays help services.
 *
 * Ideally, we would develop the Tabs component in the Shared folder
 * or create a Tabs component in the Design System that supports
 * a vertical or horizontal orientation. However, this was out of scope
 * for ENG-16210 and custom styling was also required.
 */
const HelpServicesTabs = ({ eapServices, ...props }: HelpServicesTabsProps) => {
  const { t: translate } = useTranslation('help', {
    keyPrefix: 'help_service_tabs',
  });

  const [activeServiceId, setActiveServiceId] = useState<string | null>(null);

  const { groupName } = useSubdomainInfo({
    subdomain: getSubdomainFromUrl(),
  });

  const activeService = useMemo(
    () =>
      eapServices.find(service => service.id === activeServiceId) ||
      eapServices[0],
    [activeServiceId, eapServices],
  );

  useEffect(() => {
    // Tracks the initial active service on page load
    // and when the active service changes
    tracking.track('help_eap_service_selected', {
      serviceId: activeService.id,
      serviceName: activeService.serviceName,
    });
  }, [activeService]);

  // Check for hash on mount and when eapServices load
  // This ensures that the active service can be updated when the user clicks 'Get help now'
  // even if they are already on the help page.
  useEffect(() => {
    const currentHash = window.location.hash.substring(1);

    if (currentHash && eapServices.length > 0) {
      const serviceFromHash = eapServices.find(
        service => service.icon === currentHash,
      );
      if (serviceFromHash) {
        setActiveServiceId(serviceFromHash.id);
      }
    }
  }, [eapServices]);

  const handleTabClick = (serviceId: string, serviceHash: string) => {
    setActiveServiceId(serviceId);
    window.location.hash = serviceHash; // Update hash in URL
  };

  return (
    <TabMenuContainer>
      <TabList role="tablist" aria-label={translate('tab_list.a11y_label')}>
        {eapServices.map(service => (
          <Tab
            key={service.id}
            active={service.id === activeService.id}
            aria-selected={service.id === activeService.id}
            aria-controls={`tab-content-${activeService.id}`}
            id={`tab-${service.id}`}
            onClick={() => handleTabClick(service.id, service.icon)}
            role="tab"
            tabIndex={service.id === activeService.id ? 0 : -1}
          >
            <HelpIcon icon={service.icon} />
            <TabTitle>{service.serviceName}</TabTitle>
          </Tab>
        ))}
      </TabList>
      <TabContent
        role="tabpanel"
        tabIndex={0}
        aria-labelledby={`tab-${activeService.id}`}
        id={`tab-content-${activeService.id}`}
      >
        <ServiceDetailHeader>
          <ServiceDetailTitleContainer>
            <ServiceDetailTitle>{activeService.serviceName}</ServiceDetailTitle>
            <div>
              {' '}
              {/* Ensures space-between behaves when using Design System button */}
              <ContactService activeService={activeService} {...props} />
            </div>
          </ServiceDetailTitleContainer>
          <ServiceDetailSubtitle>
            {translate('service_tab_panel.subtitle', {
              companyName: groupName,
            })}
          </ServiceDetailSubtitle>
        </ServiceDetailHeader>
        <ServiceDetailDescription
          dangerouslySetInnerHTML={{ __html: activeService.description }}
        />
      </TabContent>
    </TabMenuContainer>
  );
};

export default HelpServicesTabs;
